import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SelectField } from "@/lib/crm";
import { DOC_KINDS, Field, options } from "@/lib/assets";
import { CompanyPicker } from "@/components/sales/CompanyPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const blank = { kind: "offerta", title: "", number: "", date: "", version: "", quote_id: "", order_number: "", order_date: "", supersedes_id: "", notes: "" };

export const DocDialog = ({ open, onOpenChange, doc, presetCompany, supersede, onSaved }) => {
  const { can } = useAuth();
  const [f, setF] = useState(blank);
  const [company, setCompany] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    const src = doc || supersede;
    setCompany(src ? { id: src.company_id, name: src.company_name } : presetCompany || null);
    setF(doc ? { ...blank, kind: doc.kind, title: doc.title, number: doc.quote_id ? "" : doc.number || "", date: doc.quote_id ? "" : doc.date || "", version: doc.quote_id ? "" : doc.version || "",
      quote_id: doc.quote_id || "", order_number: doc.order_ref?.number || "", order_date: doc.order_ref?.date || "", notes: doc.notes || "" }
      : supersede ? { ...blank, kind: supersede.kind, title: supersede.title, supersedes_id: supersede.id } : blank);
  }, [open, doc, supersede, presetCompany]);
  useEffect(() => {
    if (!open || !company?.id || doc || !can("quotes:read")) { setQuotes([]); return; }
    api.get("/sales/quotes", { params: { company_id: company.id, limit: 50 } }).then((r) => setQuotes(r.data.items.map((q) => ({ key: q.id, label: `${q.number}${q.revision ? ` rev.${q.revision}` : ""} · ${q.title || ""}` })))).catch(() => setQuotes([]));
  }, [open, company?.id, doc, can]);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const v = (k) => (val) => setF((x) => ({ ...x, [k]: val }));
  const nz = (x) => x || null;
  const save = async () => {
    setBusy(true);
    const order_ref = { number: nz(f.order_number), date: nz(f.order_date) };
    try {
      if (doc) {
        const body = { title: f.title, order_ref, notes: nz(f.notes), ...(doc.quote_id ? {} : { number: nz(f.number), date: nz(f.date), version: nz(f.version) }) };
        await api.patch(`/commercial-documents/${doc.id}`, body); toast.success("Documento aggiornato"); onSaved?.(doc.id);
      } else {
        const r = await api.post("/commercial-documents", { company_id: company.id, kind: f.kind, title: f.title, quote_id: nz(f.quote_id), number: f.quote_id ? null : nz(f.number),
          date: f.quote_id ? null : nz(f.date), version: f.quote_id ? null : nz(f.version), order_ref, supersedes_id: nz(f.supersedes_id), notes: nz(f.notes) });
        toast.success(`Documento ${r.data.code} registrato`); onSaved?.(r.data.id);
      }
      onOpenChange(false);
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const fromQuote = !!f.quote_id;
  const valid = f.title.trim() && company && (fromQuote || (f.number.trim() && f.date) || doc?.quote_id);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}><DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="doc-dialog">
      <DialogHeader><DialogTitle>{doc ? `Modifica ${doc.code}` : supersede ? `Nuova versione di ${supersede.code}` : "Nuovo documento commerciale"}</DialogTitle>
        <DialogDescription>Riferimento strutturato, non un file. Se colleghi un'offerta ON! Suite, numero, data, versione e stato sono letti dall'offerta.</DialogDescription></DialogHeader>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {!doc && <div className="sm:col-span-2"><CompanyPicker value={company} onChange={setCompany} disabled={!!presetCompany || !!supersede} testId="doc-company" /></div>}
        {!doc && <SelectField label="Tipo" value={f.kind} onChange={v("kind")} options={options(DOC_KINDS)} testId="doc-kind" />}
        {!doc && <SelectField label="Offerta ON! Suite (facoltativa)" value={f.quote_id} onChange={v("quote_id")} options={quotes} allowNone="Nessuna" testId="doc-quote" />}
        <Field l="Titolo" className="sm:col-span-2"><Input value={f.title} onChange={s("title")} maxLength={200} data-testid="doc-title" /></Field>
        {!fromQuote && !doc?.quote_id && <>
          <Field l="Numero"><Input value={f.number} onChange={s("number")} data-testid="doc-number" /></Field>
          <Field l="Data"><Input type="date" value={f.date} onChange={s("date")} data-testid="doc-date" /></Field>
          <Field l="Versione"><Input value={f.version} onChange={s("version")} data-testid="doc-version" /></Field>
        </>}
        <Field l="N. ordine cliente"><Input value={f.order_number} onChange={s("order_number")} data-testid="doc-order-number" /></Field>
        <Field l="Data ordine"><Input type="date" value={f.order_date} onChange={s("order_date")} data-testid="doc-order-date" /></Field>
        <Field l="Note" className="sm:col-span-2"><Input value={f.notes} onChange={s("notes")} data-testid="doc-notes" /></Field>
      </div>
      <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button><Button onClick={save} disabled={busy || !valid} data-testid="doc-dialog-save">Salva</Button></DialogFooter>
    </DialogContent></Dialog>
  );
};
